<template>
  <div ref="linkWrap" class="link-wrap">
    <span ref="link" class="link"
          :class="{
            'bookmarked': !!pageData.bookmark,
            'display-bookmark': displayDefaultBookmark
          }"
          @mouseenter="onLinkMouseEnter"
          @mouseleave="onLinkMouseLeave"
          @mousedown.stop
          @touchstart.stop
          @click.stop
    >
      <template v-if="isCustomNode">
        <span class="url">
          {{ pageData.customId === 0 ? $t('customTag.untaggedLabel') : pageData.customValue}}
        </span>
      </template>
      <template v-else>
        <a target="_blank" :href="pageData.url">
          <i class="icon icon--ba-link blue" />
          <span class="title" :class="{noTitle: !pageData.title}" :style="{ 'max-width': urlHover ? titleMaxWidth + 'px' : 0 }">
            {{ pageData.title ? pageData.title : 'No page title' }}
          </span>
        </a>
        <span v-if="pageData.subDomain" class="subdomain" :style="{ 'max-width': subDomainMaxWidth + 'px' }">
          {{ pageData.subDomain }}
        </span>
        <span class="host">
          {{ pageData.linkHost }}
        </span>
        <span class="url">
          <template v-if="pageData.linkPath">
            <span class="front"
                  :style="{
                    'max-width': urlFrontMaxWidth + 'px'
                  }">
              {{ pageData.linkPath.substr(0, pageData.linkPath.length - backLetterLength) }}
            </span>
            <span v-if="backLetterLength"
                  class="back">
              {{ pageData.linkPath.substr(-backLetterLength) }}
            </span>
          </template>
          <span v-else class="back">/</span>
        </span>
        <template v-if="pageData.bookmark">
          <i class="icon icon--favorite-small" />
        </template>
      </template>

    </span>
  </div>
</template>

<script>
import browser from '../../libs/browser'

let urlSizeCacheMap = {}

export default {
  name: 'BaNodeLink',
  props: {
    pageData: {
      type: Object,
      required: true
    },
    displayBackLetters: {
      type: Boolean,
      default: true
    },
    displayDefaultBookmark: {
      type: Boolean,
      default: false
    },
    checkOverflowRight: {
      type: Boolean,
      default: true
    },
    isCustomNode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      urlHover: false,
      subDomainMaxWidth: Infinity,
      backLetterLength: -1
    }
  },
  computed: {
    titleMaxWidth () {
      if (!this._titleMaxWidth) return 75
      return this._titleMaxWidth
    },
    urlFrontMaxWidth () {
      if (this.urlHover) {
        return this._urlFrontFullWidth
      }
      const subDomainWidth = this._subDomainWidth || 0
      return this.backLetterLength ? (this._maxWidth - subDomainWidth) * 0.5 : this._maxWidth - subDomainWidth - 6
    }
  },
  watch: {
    pageData () {
      this.$nextTick(this.getSizes)
    },
    'pageData.bookmark' () {
      this.$nextTick(this.getSizes)
    },
    maxWidth () {
      this.$nextTick(this.getSizes)
    }
  },
  mounted () {
    this.getSizes()
    browser.isFirefox && this.$refs.link.classList.add('firefox')
  },
  methods: {
    onLinkMouseEnter (e) {
      if (this.checkOverflowRight) {
        const rect = this.$refs.linkWrap.getBoundingClientRect()
        let openedRight = this._titleMaxWidth + this._urlFullWidth + 150
        if (this.bookmark) {
          openedRight += 20
        }
        openedRight *= (rect.right - rect.left) / this.$refs.linkWrap.offsetWidth
        openedRight += rect.left
        this.$refs.link.classList[openedRight > window.innerWidth ? 'add' : 'remove']('right')
      }
      this.urlHover = true
      this.$emit('onMouseEnter', e)
    },
    onLinkMouseLeave (e) {
      this.urlHover = false
      this.$emit('onMouseLeave', e)
    },
    getSizes () {
      this._maxWidth = this.$refs.linkWrap.offsetWidth
      const cacheKey = this._maxWidth + (this.pageData.bookmark ? '-bookmark' : '')
      if (!urlSizeCacheMap[cacheKey]) {
        urlSizeCacheMap[cacheKey] = {}
      }
      if (this.pageData.url && (/^http/).test(this.pageData.url) && this.$refs.link) {
        const $link = this.$refs.link

        clearTimeout(this._enableTransitionTimer)
        $link.classList.add('no-transition')

        if (urlSizeCacheMap[cacheKey][this.pageData.url]) {
          const sizeMap = urlSizeCacheMap[cacheKey][this.pageData.url]
          this.subDomainMaxWidth = sizeMap.subDomainMaxWidth
          this._subDomainWidth = sizeMap.subDomainWidth
          this._urlFullWidth = sizeMap.urlFullWidth
          this._titleMaxWidth = sizeMap.titleMaxWidth
          this.backLetterLength = sizeMap.backLetterLength
          this._urlFrontFullWidth = sizeMap.urlFrontFullWidth
        } else {
          let $tester
          const $subDomain = $link.querySelector('.subdomain')
          const realMaxWidth = this._maxWidth - (this.displayDefaultBookmark && this.pageData.bookmark ? 20 : 0)
          this._subDomainWidth = 0
          if ($subDomain) {
            this.subDomainMaxWidth = Math.max(38, Math.round(realMaxWidth * 0.3))
            $tester = $subDomain.cloneNode(true)
            $tester.style.maxWidth = this.subDomainMaxWidth + 'px'
            $link.appendChild($tester)
            this._subDomainWidth = Math.min($tester.offsetWidth, this.subDomainMaxWidth)
            $link.removeChild($tester)
          }

          $tester = $link.querySelector('.title').cloneNode(true)
          $tester.innerHTML = this.pageData.title ? this.pageData.title : 'No page title'
          $tester.style.maxWidth = '999em'
          $link.appendChild($tester)
          this._titleMaxWidth = $tester.offsetWidth + 10
          $link.removeChild($tester)

          $tester = $link.querySelector('.url').cloneNode(true)
          $tester.innerHTML = this.pageData.linkPath
          $link.appendChild($tester)
          this._urlFullWidth = $tester.offsetWidth

          const linkWrapWidth = this._subDomainWidth + $tester.offsetWidth + 6

          this.backLetterLength = 0
          if (linkWrapWidth > realMaxWidth) {
            const linkPath = this.pageData.linkPath
            if (this.displayBackLetters) {
              const backLetterMaxWidth = (realMaxWidth - this._subDomainWidth) * 0.5 - 6
              $tester.innerHTML = ''
              for (let i = linkPath.length - 1; i > -1; i--) {
                $tester.innerHTML = linkPath[i] + $tester.innerHTML
                if ($tester.offsetWidth > backLetterMaxWidth) {
                  this.backLetterLength = linkPath.length - i - 1
                  break
                }
              }
            }
            $tester.innerHTML = this.pageData.linkPath.substr(0, linkPath.length - this.backLetterLength)
          }
          this._urlFrontFullWidth = $tester.offsetWidth + 9

          urlSizeCacheMap[cacheKey][this.pageData.url] = {
            subDomainMaxWidth: this.subDomainMaxWidth,
            subDomainWidth: this._subDomainWidth,
            titleMaxWidth: this._titleMaxWidth,
            urlFullWidth: this._urlFullWidth,
            backLetterLength: this.backLetterLength,
            urlFrontFullWidth: this._urlFrontFullWidth
          }

          $link.removeChild($tester)
          $tester = null
        }

        this._enableTransitionTimer = setTimeout(this.enableTransitions, 200)
      }
    },
    enableTransitions () {
      if (this.$refs.link) {
        this.$refs.link.classList.remove('no-transition')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.link-wrap {
  position: absolute;
  left: 30px;
  right: 6px;
  top: 50%;
  height: 22px;
  color: #fff;
  transform: translateY(-50%);
}
.link {
  position: absolute;
  left: 0;
  top: 0;
  min-width: 100%;
  height: 22px;
  line-height: 16px;
  white-space: nowrap;
  padding-top: 3px;
  padding-bottom: 1px;
  border-radius: 4px;
  box-sizing: border-box;
  display: block;
  pointer-events: auto;
  transition: 0.3s ease;
  transition-property: padding, background, box-shadow, transform;
  cursor: text;

  &.right {
    right: 0;
    left: auto;
    &:hover {
      // transform: translateX(4px);
    }
  }

  &.firefox {
    line-height: 14px;
  }

  body.dragging & {
    pointer-events: none;
  }

  &.no-transition,
  &.no-transition * {
    transition: none !important;
  }

  .title {
    display: inline-block;
    vertical-align: top;
    max-width: 0;
    overflow: hidden;
    transition: max-width 0.3s ease,
                margin-right 0.3s ease,
                padding-right 0.3s ease,
                border-right-width 0.3s ease,
                opacity 0.3s;
    border-right: 0 solid #dddddd;
    color: #555;
    opacity: 0;

    &.noTitle {
      font-style: italic;
      color: #888;
    }
  }

  .subdomain {
    max-width: 58px;
    height: 16px;
    color: #767676;
    white-space: nowrap;
    vertical-align: top;
    text-overflow: ellipsis;
    margin-right: 4px;
    padding: 0 4px;
    border-radius: 4px;
    box-sizing: border-box;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.144);
    background-color: rgba(255, 255, 255, 0.9);
    display: inline-block;
    overflow: hidden;
    transition: max-width 0.15s ease,
                padding 0.3s ease,
                margin-right 0.3s ease,
                opacity 0.3s ease;
  }

  .host {
    display: inline-block;
    vertical-align: top;
    max-width: 0;
    overflow: hidden;
    transition: max-width 0.3s ease;
  }

  .front {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: top;
    transition: max-width 0.3s ease,
                color 0.3s ease;
  }

  .back {
    display: inline-block;
    min-width: 60px;
    vertical-align: top;
    transition: min-width 0.3s ease,
                color 0.3s ease;
  }

  .icon--ba-link {
    vertical-align: top;
    margin: 0 0 0;
    width: 0;
    opacity: 0;
    transition: width 0.3s ease,
                margin 0.3s ease,
                opacity 0.3s ease;
  }

  .icon--favorite-small {
    position: absolute;
    right: -2px;
    top: 3px;
    pointer-events: none;
    transition: opacity 0.1s ease, transform 0.15s ease;
  }

  &:not(.display-bookmark) {
    .icon--favorite-small {
      opacity: 0;
    }
  }

  &:hover {
    color: #555;
    padding-right: 10px;
    background: #ffffff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.16);
    // transform: translateX(-4px);

    &.bookmarked {
      padding-right: 30px;
    }

    .title {
      margin-right: 5px;
      padding-right: 5px;
      border-right-width: 1px;
      font-weight: 500;
      opacity: 1;
    }

    .subdomain {
      max-width: 0 !important;
      padding: 0;
      opacity: 0;
      margin-right: 0;
    }

    .host {
      max-width: 300px;
      font-weight: 500;
    }

    .front {
      max-width: 800px;
      font-weight: 500;
    }

    .back {
      min-width: 30px;
      font-weight: 500;
    }

    .icon--ba-link {
      width: 16px;
      margin: 0 5px 0 4px;
      opacity: 1;
    }

    .icon--favorite-small {
      opacity: 1;
      transform: translate3d(-8px, 0, 0);
    }
  }

  &:not(:hover) {
    user-select: none;
  }
}
</style>
